import Holidays from 'date-holidays'
import { isGrantedRole } from '@/utils/security'
import { ROLE_ADMIN, ROLE_AGENT, ROLE_CLIENT, ROLE_MANAGER } from '@/consts/app'
import { getCurrency, ln2brConverter } from '@/utils'
import { INTERVENTION_REPORTS_DEFAULT_TEMPLATE } from '@/consts/interventionReports'
import { DATA_BASE_AGENCY } from '@/consts/data/agencies'

export default {
  /**
   * Détermine si l'utilisateur est connecté
   * @param state
   * @returns {boolean}
   */
  isLoggedIn: (state) => state.authToken !== null && state.utilisateur.enabled,

  /**
   * Récupère les droits de l'utilisateur
   * @param state
   * @returns {any}
   */
  rights: (state) => {
    return state.utilisateur.rights || {}
  },

  /**
   * Vérifie si l'utilisateur bénéficie au moins d'un des droits
   * @param state
   * @param getters
   * @returns {function(*=): (function(): Boolean)|string|boolean}
   */
  isGranted: (state, getters) => (rights = []) => {
    return getters.isAgencyOwner ||
      (Array.isArray(rights)
        ? rights
        : [rights]).some(({ value } = {}) => getters.rights[value]) ||
      getters.isAdmin
  },

  /**
   * Vérifie que l'utilisateur bénéficie des autorisations liées au site
   * @param state
   * @param getters
   * @return {function(*, *=): *}
   */
  isGrantedSite: (state, getters) => (site, rights = []) => {
    if (getters.isManager || getters.isAdmin) return true
    const kRights = getters.isCustomer
      ? 'rightsCustomer'
      : 'rightsAgent'
    const _rights = site[kRights] || {}
    return (
      Array.isArray(rights)
        ? rights
        : [rights]
    ).some(({ value } = {}) => _rights[value] || false)
  },

  /**
   * L'utilisateur est il agent ?
   * @returns {Boolean}
   */
  isAgent () {
    return isGrantedRole([ROLE_AGENT])
  },

  /**
   * L'utilsiateur est il manager ?
   * @returns {Boolean}
   */
  isManager () {
    return isGrantedRole([ROLE_MANAGER])
  },
  /**
   * Droits supérieurs pour le propriétaire de l'agence
   *
   * @param utilisateur
   * @param company
   * @param isAdmin
   * @return {boolean}
   */
  isAgencyOwner: ({ utilisateur }, { company, isAdmin }) => {
    return _.get(company, ['manager', 'id']) === utilisateur.id || isAdmin
  },

  /**
   * L'utilsiateur est il client ?
   * @returns {Boolean}
   */
  isCustomer () {
    return isGrantedRole([ROLE_CLIENT])
  },

  /**
   * L'utilsiateur est il admin ?
   * @returns {Boolean}
   */
  isAdmin () {
    return isGrantedRole([ROLE_ADMIN])
  },

  /**
   * Récupère le token
   * @param state
   * @returns {null|Object}
   */
  authToken: (state) => state.authToken,

  /**
   * Récupère le slug agent
   * @param state
   * @returns {String}
   */
  agentSlug: (state) => _.get(state.utilisateur, ['slug'], ''),

  /**
   * Récupère l'id de l'utilisateur
   * @param state
   * @returns {*}
   */
  userId: (state) => _.get(state.utilisateur, ['id'], null),

  /**
   * Récupère l'agence
   * @param state
   * @param getters
   * @returns {Object}
   */
  company: (state, getters) => {
    // TODO script migration en bdd
    const agency = state.alteredAgency
      ? getters.companies.find((c) => c.slug === state.alteredAgency) || {}
      : _.get(state.utilisateur, getters.isCustomer ? ['company', 'agency'] : ['company'], {})
    const estimateSettings = ln2brConverter(
      agency.estimateSettings,
      ['header', 'footer', 'bankDetails', 'latePaymentMessage', { key: 'email', keys: ['message'] }])
    const invoiceSettings = ln2brConverter(
      agency.invoiceSettings,
      ['header', 'footer', 'bankDetails', 'latePaymentMessage', { key: 'email', keys: ['message'] }])
    const interventionReportTemplate = agency.interventionReportTemplate || _.cloneDeep(INTERVENTION_REPORTS_DEFAULT_TEMPLATE)
    const eventReportSettings = ln2brConverter(
      agency.eventReportSettings,
      ['header', 'footer', { key: 'email', keys: ['message'] }])
    const interventionReportSettings = ln2brConverter(
      agency.interventionReportSettings,
      ['header', 'footer', { key: 'email', keys: ['message'] }])
    const agentPlanningSettings = ln2brConverter(
      agency.agentPlanningSettings,
      ['header', 'footer', { key: 'email', keys: ['message'] }])
    const customerPlanningSettings = ln2brConverter(
      agency.customerPlanningSettings,
      ['header', 'footer', { key: 'email', keys: ['message'] }])
    const agentExpirationSettings = ln2brConverter(
      agency.agentExpirationSettings,
      ['header', 'footer', { key: 'email', keys: ['message'] }])
    return {
      ..._.cloneDeep(DATA_BASE_AGENCY),
      ...agency,
      address: {
        ...(agency.address || {})
      },
      estimateSettings,
      invoiceSettings,
      interventionReportTemplate,
      eventReportSettings,
      interventionReportSettings,
      agentPlanningSettings,
      customerPlanningSettings,
      agentExpirationSettings
    }
  },

  /**
   * Récupère le client
   */
  customer: (state) => {
    return state.utilisateur.company || {}
  },

  /**
   * Récupère la formule de l'agence
   * @param state
   * @param getters
   */
  subscription: (state, getters) => {
    return getters.company.subscription
  },

  /**
   * Vérifie si l'agence a est ok niveau paiement
   * @param state
   * @param getters
   * @returns {*|boolean}
   */
  hasValidSubscription: (state, getters) => {
    if (getters.isAdmin) {
      return true
    }
    const { subscription, company } = getters
    // const now = dateTimeFormatIso(new Date()) TODO ajouter 7 jours
    return !!(subscription &&
      company.status === 100 &&
      subscription.active
      // subscription.paid &&
      // !subscription.error &&
      // && subscription.currentPeriodEndAt > now
    )
  },

  isAgentsLimitReached: (state, getters, rootState) => {
    const { pricing } = rootState.user.agency
    return pricing.countAgents.agents >= pricing.agentsLimit
  },

  isAccountsLimitReached: (state, getters, rootState) => {
    const { pricing } = rootState.user.agency
    return pricing.countAgents.accounts >= pricing.accountsLimit
  },

  isFirstLogin: (state, getters) => {
    return state.utilisateur.firstLogin && getters.hasValidSubscription
  },

  /**
   * Récupère une instance de date-holiday
   * @param state
   * @param holidayLocale
   * @returns {Holidays}
   */
  holidaysInstance: (state, { company: { holidayLocale = '' } }) => new Holidays(...holidayLocale.split('-')),

  /**
   * Récuoère le slug de l'agence
   * @param state
   * @param getters
   * @returns {String}
   */
  agencySlug: (state, getters) => state.alteredAgency || _.get(getters.company, ['slug'], ''),

  /**
   * Récuoère le pays de l'agence
   * @param state
   * @param getters
   * @returns {String}
   */
  agencyCountry: (state, getters) => _.get(getters.company, ['address', 'country'], 'France'),

  /**
   * Récuoère le slug du client
   * @param state
   * @param getters
   * @returns {String}
   */
  customerSlug: (state, getters) => getters.customer.slug || '',

  /**
   * Récupère les informations sur la devise de l'agence
   *
   * @param state
   * @param getters
   * @returns {*}
   */
  companyCurrency: (state, getters) => getCurrency(getters.company.currency),

  /**
   * Détermine si l'agence utilise la taxe cnaps
   *
   * @param state
   * @param getters
   * @returns {*}
   */
  companyCnapsCount: (state, getters) => getters.company.cnapsCount,

  /**
   * Liste des agences de l'utilisateur connecté
   * @param state
   * @param isAdmin
   * @returns {*}
   */
  companies: (state, { isAdmin }) => isAdmin
    ? state.agency.agencies
    : _.get(state.utilisateur, ['companies'], []),

  /**
   * Retourne la liste des companyEventTypes indexés par serviceEventType.id
   * @param state
   * @param getters
   */
  indexedCmpSrvEvt: (state, getters) => {
    return _.keyBy(getters.company.serviceEventTypes, 'serviceEventType.id')
  },

  /**
   * Retourne la liste des chiens
   */
  dogs: (state) => _.get(state.utilisateur, ['agentInfos', 'dogs'], [])
}
